import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import Badge from '../../components/ui/Badge';
import { ArrowLeft, Edit2, Trash2, MapPin, Calendar, Car, User, FileText, ParkingCircle, LogOut } from 'lucide-react';
import { visitorLogService, parkingService } from '../../services';
import type { VisitorLog } from '../../types';
import { format } from 'date-fns';

const ViewEntryExitPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [log, setLog] = useState<VisitorLog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vehicleDetails, setVehicleDetails] = useState<any>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [processingExit, setProcessingExit] = useState(false);

  useEffect(() => {
    if (id) {
      fetchLog(id);
    }
  }, [id]);

  const fetchLog = async (logId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await visitorLogService.getVisitorLogById(logId);
      console.log('Full log response:', response);
      console.log('Parking data:', {
        parking_lot_id: response.parking_lot_id,
        parking_spot_id: response.parking_spot_id,
        parking_spot_number: response.parking_spot_number,
        parking_spot_id_2: response.parking_spot_id_2,
        parking_spot_number_2: response.parking_spot_number_2
      });
      setLog(response);
      
      // Parse vehicle_details JSON if it exists
      if ((response as any).vehicle_details) {
        try {
          const parsed = JSON.parse((response as any).vehicle_details);
          setVehicleDetails(parsed);
          console.log('Parsed vehicle_details:', parsed);
        } catch (err) {
          console.error('Failed to parse vehicle_details:', err);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch log');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await visitorLogService.deleteVisitorLog(id);
      navigate('/entry-exit');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete log');
    }
  };

  // Handle vehicle exit - free the parking spot and create EXIT log
  const handleVehicleExit = async () => {
    if (!log || !id) return;
    
    try {
      setProcessingExit(true);
      setError(null);

      // Free the parking spot if one was assigned
      if (log.parking_spot_id) {
        try {
          await parkingService.freeSpot(log.parking_spot_id);
          console.log('Parking spot freed');
        } catch (parkingErr) {
          console.error('Failed to free parking spot:', parkingErr);
          // Continue even if freeing spot fails
        }
      }

      // Create an EXIT log based on this ENTRY log
      const exitPayload: any = {
        location_id: log.location_id,
        log_access_type: 'EXIT',
        log_operator_type: log.log_operator_type,
        log_capture_type: 'MANUAL',
        has_vehicle: (log as any).has_vehicle || '1',
        log_license_plate: log.log_license_plate,
        log_vehicle_type: (log as any).log_vehicle_type,
        log_vehicle_make: (log as any).log_vehicle_make,
        log_vehicle_model: log.log_vehicle_model,
        log_vehicle_color: log.log_vehicle_color,
        log_external_driver_name: (log as any).log_external_driver_name,
        log_visitor_company: (log as any).log_visitor_company,
        log_note: `Exit for Entry Log #${log.log_id}`,
      };

      // Remove undefined/null values
      Object.keys(exitPayload).forEach(key => {
        if (exitPayload[key] === undefined || exitPayload[key] === null) {
          delete exitPayload[key];
        }
      });

      const exitLog = await visitorLogService.createVisitorLog(exitPayload);
      
      setShowExitConfirm(false);
      navigate(`/entry-exit/${exitLog.log_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process exit');
    } finally {
      setProcessingExit(false);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MMM d, yyyy h:mm:ss a');
    } catch {
      return 'Invalid date';
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Loading size="lg" text="Loading log details..." />
        </div>
      </MainLayout>
    );
  }

  if (error || !log) {
    return (
      <MainLayout>
        <Alert variant="error" title="Error" message={error || 'Log not found'} />
        <Button
          variant="outline"
          leftIcon={<ArrowLeft size={18} />}
          onClick={() => navigate('/entry-exit')}
          className="mt-4"
        >
          Back to Entry/Exit Logs
        </Button>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/entry-exit')}
              leftIcon={<ArrowLeft size={18} />}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Entry/Exit Log Details</h1>
              <p className="text-sm text-text-muted mt-1">Log ID: {log.log_id}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              leftIcon={<Edit2 size={18} />}
              onClick={() => navigate(`/entry-exit/${id}/edit`)}
            >
              Edit
            </Button>
            {log.log_access_type === 'ENTRY' && (
              <Button
                variant="primary"
                leftIcon={<LogOut size={18} />}
                onClick={() => setShowExitConfirm(true)}
              >
                Exit Vehicle
              </Button>
            )}
            <Button
              variant="danger"
              leftIcon={<Trash2 size={18} />}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </Button>
          </div>
        </div>

        {error && <Alert variant="error" title="Error" message={error} />}

        {/* Photo Section */}
        {(log.log_image || log.log_license_image || log.log_url) && (
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-4">Captured Photos</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {log.log_license_image && (
                <div>
                  <p className="text-sm text-text-muted mb-2 font-medium">ID Photo</p>
                  <img
                    src={log.log_license_image}
                    alt="ID"
                    className="w-full h-auto rounded-lg shadow-md"
                    style={{ maxHeight: '300px', objectFit: 'cover' }}
                  />
                </div>
              )}
              {log.log_image && (
                <div>
                  <p className="text-sm text-text-muted mb-2 font-medium">Face Photo</p>
                  <img
                    src={log.log_image}
                    alt="Face"
                    className="w-full h-auto rounded-lg shadow-md"
                    style={{ maxHeight: '300px', objectFit: 'cover' }}
                  />
                </div>
              )}
              {log.log_url && (
                <div>
                  <p className="text-sm text-text-muted mb-2 font-medium">License Plate Photo</p>
                  <img
                    src={log.log_url}
                    alt="License Plate"
                    className="w-full h-auto rounded-lg shadow-md"
                    style={{ maxHeight: '300px', objectFit: 'cover' }}
                  />
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Vehicle Photos Section */}
        {vehicleDetails && (vehicleDetails.log_vehicle_image_front || vehicleDetails.log_vehicle_image_right || 
         vehicleDetails.log_vehicle_image_left || vehicleDetails.log_vehicle_image_back) && (
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-4">Vehicle Photos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {vehicleDetails.log_vehicle_image_front && (
                <div>
                  <p className="text-sm text-text-muted mb-2 font-medium">Front</p>
                  <img
                    src={vehicleDetails.log_vehicle_image_front}
                    alt="Vehicle Front"
                    className="w-full h-auto rounded-lg shadow-md"
                    style={{ maxHeight: '200px', objectFit: 'cover' }}
                  />
                </div>
              )}
              {vehicleDetails.log_vehicle_image_right && (
                <div>
                  <p className="text-sm text-text-muted mb-2 font-medium">Right Side</p>
                  <img
                    src={vehicleDetails.log_vehicle_image_right}
                    alt="Vehicle Right"
                    className="w-full h-auto rounded-lg shadow-md"
                    style={{ maxHeight: '200px', objectFit: 'cover' }}
                  />
                </div>
              )}
              {vehicleDetails.log_vehicle_image_left && (
                <div>
                  <p className="text-sm text-text-muted mb-2 font-medium">Left Side</p>
                  <img
                    src={vehicleDetails.log_vehicle_image_left}
                    alt="Vehicle Left"
                    className="w-full h-auto rounded-lg shadow-md"
                    style={{ maxHeight: '200px', objectFit: 'cover' }}
                  />
                </div>
              )}
              {vehicleDetails.log_vehicle_image_back && (
                <div>
                  <p className="text-sm text-text-muted mb-2 font-medium">Back</p>
                  <img
                    src={vehicleDetails.log_vehicle_image_back}
                    alt="Vehicle Back"
                    className="w-full h-auto rounded-lg shadow-md"
                    style={{ maxHeight: '200px', objectFit: 'cover' }}
                  />
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Log Details */}
        <Card>
          <h3 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                <FileText size={16} />
                <span>Access Type</span>
              </div>
              <Badge
                variant={log.log_access_type === 'ENTRY' ? 'success' : 'warning'}
                size="lg"
              >
                {log.log_access_type}
              </Badge>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                <User size={16} />
                <span>Entry/Exit By</span>
              </div>
              <Badge variant="default" size="lg">
                {log.log_operator_type}
              </Badge>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                <FileText size={16} />
                <span>Capture Type</span>
              </div>
              <Badge variant={log.log_capture_type === 'AUTO' ? 'success' : 'info'} size="md">
                {log.log_capture_type}
              </Badge>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                <MapPin size={16} />
                <span>Location</span>
              </div>
              <div className="text-text-primary font-medium">
                {log.location_name || `Location #${log.location_id}`}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                <Calendar size={16} />
                <span>Date/Time</span>
              </div>
              <div className="text-text-primary">{formatDate((log as any).created_date || log.log_capture_time)}</div>
            </div>

            <div>
              <div className="text-sm text-text-muted mb-1">Has Vehicle</div>
              <Badge variant={(log as any).has_vehicle === '1' ? 'success' : 'default'}>
                {(log as any).has_vehicle === '1' ? 'Yes' : 'No'}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Vehicle Information */}
        {(log as any).has_vehicle === '1' && (
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-4">Vehicle Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                  <Car size={16} />
                  <span>License Plate</span>
                </div>
                <div className="text-text-primary font-medium text-lg">
                  {log.log_license_plate || 'N/A'}
                </div>
              </div>

              <div>
                <div className="text-sm text-text-muted mb-1">Vehicle Type</div>
                <div className="text-text-primary">{(log as any).log_vehicle_type || 'N/A'}</div>
              </div>

              <div>
                <div className="text-sm text-text-muted mb-1">Vehicle Make</div>
                <div className="text-text-primary">{(log as any).log_vehicle_make || 'N/A'}</div>
              </div>

              <div>
                <div className="text-sm text-text-muted mb-1">Vehicle Model</div>
                <div className="text-text-primary">{log.log_vehicle_model || 'N/A'}</div>
              </div>

              <div>
                <div className="text-sm text-text-muted mb-1">Vehicle Color</div>
                <div className="text-text-primary">{log.log_vehicle_color || 'N/A'}</div>
              </div>

              {(log as any).log_truck && (
                <div>
                  <div className="text-sm text-text-muted mb-1">Truck Number</div>
                  <div className="text-text-primary">{(log as any).log_truck}</div>
                </div>
              )}

              {(log as any).log_trailer && (
                <div>
                  <div className="text-sm text-text-muted mb-1">Trailer Number</div>
                  <div className="text-text-primary">{(log as any).log_trailer}</div>
                </div>
              )}

              {(log as any).log_trailer_plate && (
                <div>
                  <div className="text-sm text-text-muted mb-1">Trailer Plate</div>
                  <div className="text-text-primary">{(log as any).log_trailer_plate}</div>
                </div>
              )}

              {(log as any).log_damages && (
                <div className="md:col-span-3">
                  <div className="text-sm text-text-muted mb-1">Vehicle Damages</div>
                  <div className="text-text-primary">{(log as any).log_damages}</div>
                </div>
              )}
            </div>

            {/* Vehicle Inspection Details */}
            {vehicleDetails && (vehicleDetails.access_point || vehicleDetails.inspection_status || vehicleDetails.inspected_by) && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-md font-semibold text-text-primary mb-4">Inspection Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {vehicleDetails.access_point && (
                    <div>
                      <div className="text-sm text-text-muted mb-1">Access Point</div>
                      <div className="text-text-primary">{vehicleDetails.access_point}</div>
                    </div>
                  )}
                  {vehicleDetails.inspection_status && (
                    <div>
                      <div className="text-sm text-text-muted mb-1">Inspection Status</div>
                      <Badge variant={vehicleDetails.inspection_status === 'Ok' ? 'success' : 'warning'}>
                        {vehicleDetails.inspection_status}
                      </Badge>
                    </div>
                  )}
                  {vehicleDetails.inspected_by && (
                    <div>
                      <div className="text-sm text-text-muted mb-1">Inspected By</div>
                      <div className="text-text-primary">{vehicleDetails.inspected_by}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Parking Information */}
        {(log.parking_lot_id || log.parking_spot_number) && (
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <ParkingCircle size={20} className="text-primary-500" />
              <h3 className="text-lg font-semibold text-text-primary">Parking Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {log.parking_spot_number && (
                <div>
                  <div className="text-sm text-text-muted mb-1">Truck Spot #</div>
                  <div className="text-text-primary font-medium text-lg">
                    {log.parking_spot_number}
                  </div>
                </div>
              )}
              {log.parking_spot_number_2 && (
                <div>
                  <div className="text-sm text-text-muted mb-1">Trailer Spot #</div>
                  <div className="text-text-primary font-medium text-lg">
                    {log.parking_spot_number_2}
                  </div>
                </div>
              )}
              {log.parking_lot_id && (
                <div>
                  <div className="text-sm text-text-muted mb-1">Lot ID</div>
                  <div className="text-text-primary">#{log.parking_lot_id}</div>
                </div>
              )}
              {log.log_access_type === 'ENTRY' && (
                <div>
                  <div className="text-sm text-text-muted mb-1">Status</div>
                  <Badge variant="info">Vehicle Parked</Badge>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Driver/Visitor Information */}
        <Card>
          <h3 className="text-lg font-semibold text-text-primary mb-4">
            {log.log_operator_type === 'VISITOR' ? 'Visitor Information' : 'Driver Information'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(log as any).log_external_driver_name && (
              <div>
                <div className="text-sm text-text-muted mb-1">Name</div>
                <div className="text-text-primary">{(log as any).log_external_driver_name}</div>
              </div>
            )}

            {(log as any).log_driver_license_no && (
              <div>
                <div className="text-sm text-text-muted mb-1">Driver License No</div>
                <div className="text-text-primary">{(log as any).log_driver_license_no}</div>
              </div>
            )}

            {(log as any).log_driver_email && (
              <div>
                <div className="text-sm text-text-muted mb-1">Email</div>
                <div className="text-text-primary">{(log as any).log_driver_email}</div>
              </div>
            )}

            {(log as any).log_visitor_company && (
              <div>
                <div className="text-sm text-text-muted mb-1">Company</div>
                <div className="text-text-primary">{(log as any).log_visitor_company}</div>
              </div>
            )}

            {(log as any).log_visitor_phone_number && (
              <div>
                <div className="text-sm text-text-muted mb-1">Phone Number</div>
                <div className="text-text-primary">{(log as any).log_visitor_phone_number}</div>
              </div>
            )}

            {(log as any).log_visitor_purpose && (
              <div>
                <div className="text-sm text-text-muted mb-1">Purpose</div>
                <div className="text-text-primary">{(log as any).log_visitor_purpose}</div>
              </div>
            )}

            {(log as any).log_safety_vest && (
              <div>
                <div className="text-sm text-text-muted mb-1">Safety Vest</div>
                <Badge variant={(log as any).log_safety_vest === 'Yes' ? 'success' : 'warning'}>
                  {(log as any).log_safety_vest}
                </Badge>
              </div>
            )}
          </div>
        </Card>

        {/* Notes */}
        {log.log_note && (
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-3">Notes</h3>
            <div className="text-text-secondary whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
              {log.log_note}
            </div>
          </Card>
        )}

        {/* Metadata */}
        <Card>
          <h3 className="text-lg font-semibold text-text-primary mb-3">Metadata</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-text-muted">Created:</span>{' '}
              <span className="text-text-primary">{formatDate(log.created_at)}</span>
            </div>
            {log.created_by && (
              <div>
                <span className="text-text-muted">Created By:</span>{' '}
                <span className="text-text-primary">{log.created_by}</span>
              </div>
            )}
            {log.updated_at && (
              <div>
                <span className="text-text-muted">Updated:</span>{' '}
                <span className="text-text-primary">{formatDate(log.updated_at)}</span>
              </div>
            )}
            {log.updated_by && (
              <div>
                <span className="text-text-muted">Updated By:</span>{' '}
                <span className="text-text-primary">{log.updated_by}</span>
              </div>
            )}
          </div>
        </Card>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Delete Log</h2>
              <p className="text-gray-900 mb-4">
                Are you sure you want to delete this entry/exit log?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-medium">⚠️ Warning</p>
                <p className="text-sm text-red-700 mt-1">This action cannot be undone.</p>
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Exit Vehicle Confirmation Modal */}
        {showExitConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Exit Vehicle</h2>
              <p className="text-gray-900 mb-4">
                Process exit for vehicle <span className="font-semibold">{log.log_license_plate}</span>?
              </p>
              {log.parking_spot_number && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 font-medium">
                    <ParkingCircle size={16} className="inline mr-1" />
                    Truck Spot: {log.parking_spot_number}
                    {log.parking_spot_number_2 && `, Trailer Spot: ${log.parking_spot_number_2}`}
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    {log.parking_spot_number_2 ? 'These spots will be marked as available.' : 'This spot will be marked as available.'}
                  </p>
                </div>
              )}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800">
                  This will create a new EXIT log for this vehicle.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setShowExitConfirm(false)}
                  disabled={processingExit}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleVehicleExit}
                  disabled={processingExit}
                  leftIcon={processingExit ? <Loading size="sm" /> : <LogOut size={18} />}
                >
                  {processingExit ? 'Processing...' : 'Confirm Exit'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ViewEntryExitPage;
